import { schedulerCalendarDateKey } from './timeOffCalendarHolidays';
import type { TimeOffSchedulerEvent } from './timeOffCalendarTypes';

export interface TimeOffAgendaDayGroup {
  dateKey: string;
  label: string;
  events: TimeOffSchedulerEvent[];
}

function agendaDayLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function eventDateKeys(event: TimeOffSchedulerEvent): string[] {
  if (!event.allDay) {
    return [schedulerCalendarDateKey(event.startDate)];
  }

  const keys: string[] = [];
  const cursor = new Date(event.startDate);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(event.endDate);
  end.setHours(0, 0, 0, 0);

  while (cursor < end) {
    keys.push(schedulerCalendarDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return keys.length > 0 ? keys : [schedulerCalendarDateKey(event.startDate)];
}

function eventIntersectsRange(event: TimeOffSchedulerEvent, rangeStart: Date, rangeEnd: Date): boolean {
  const startMs = event.startDate.getTime();
  const endMs = event.endDate.getTime();
  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();
  return startMs <= rangeEndMs && endMs >= rangeStartMs;
}

/** Keep agenda entries that overlap the visible month grid (including spillover days). */
export function filterSchedulerEventsInRange(
  events: TimeOffSchedulerEvent[],
  rangeStart: Date,
  rangeEnd: Date
): TimeOffSchedulerEvent[] {
  return events.filter((event) => eventIntersectsRange(event, rangeStart, rangeEnd));
}

export function groupSchedulerEventsByDate(events: TimeOffSchedulerEvent[]): TimeOffAgendaDayGroup[] {
  const byDate = new Map<string, TimeOffSchedulerEvent[]>();

  for (const event of events) {
    for (const dateKey of eventDateKeys(event)) {
      const bucket = byDate.get(dateKey);
      if (bucket) bucket.push(event);
      else byDate.set(dateKey, [event]);
    }
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, dayEvents]) => ({
      dateKey,
      label: agendaDayLabel(dateKey),
      events: [...dayEvents].sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }))
    }));
}

export function formatAgendaEventRange(event: TimeOffSchedulerEvent): string {
  if (event.allDay) return 'All day';

  const start = event.startDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const end = event.endDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${start} – ${end}`;
}
